import { Component, OnInit, Renderer2 } from '@angular/core';
import { ModalService } from '../../../services/modal/modal.service';
import { ClientEsignModalComponent } from './client-esign-modal.component';
import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import { AccountCenterService } from '../../../services/account-center/account-center.service';
import { PolicyData } from '../../../interfaces/PolicyData';
import { BasicIllustrationService } from '../../../services/basicillustration/BasicIllustrationService';
import { IllustrationRequest } from '../../../../../coverpath-common/classes/wholelife/IllustrationRequest';
import { Utils } from '../../../../../common/js/Utils';
import type { CoverpathBasePolicy } from '../../../../classes/coverpath_subclasses/CoverpathBasePolicy';
import { IsLoadingType } from '../../../interfaces/account-center/IsLoadingDataInterface';
import type { CoverpathPolicy } from '../../../../../coverpath-common/classes/coverpath_subclasses/CoverpathPolicy';
import { CoverpathUtils } from '../../../../../coverpath-common/js/static/CoverpathUtils';
import { UICategory, UILoggerSingleton } from '../../../../../common/js/logger/UILoggerSingleton';
interface FormToSign {
  formType: string;
  pdfUrl: string;
  textPrompt: string;
  signFn(): void;
}
@Component({
  selector: 'client-conversion-esign',
  moduleId: module.id,
  templateUrl: './client-esign-modal.component.html',
})
export class ClientConversionEsignModalComponent extends ClientEsignModalComponent implements OnInit {
  constructor(
    amorphicService: AmorphicService,
    modalService: ModalService,
    renderer: Renderer2,
    accountCenterService: AccountCenterService,
    private basicIllustrationService: BasicIllustrationService
  ) {
    super(amorphicService, modalService, renderer, accountCenterService);
  }

  async determineFormsToSign(primaryPolicyNumber: string, primaryPolicyData: PolicyData): Promise<Array<FormToSign>> {
    const formsToSign: Array<FormToSign> = [];
    let floridaStateLicense;
    let agentCountyCityStateString;
    const policy = this.controller.customer.application.primaryPolicy;
    if (policy.ownerPersonResidentialAddress && policy.ownerPersonResidentialAddress.state === 'FL') {
      const producerId = policy.primaryPolicyAgent.agent.producerId;
      const agentLicenseList = await this.controller.getStateLicenses(policy.primaryPolicyAgent.agent.producerId);
      floridaStateLicense = CoverpathUtils.getFloridaLicense(agentLicenseList, producerId);
      agentCountyCityStateString = await this.getAgentCountyCityStateString(policy.ownerPersonResidentialAddress.zip);
    }
    const needsToSignConversionForm = await this.controller.accountCenterController.customerNeedsToSignConversionForm(
      policy,
      floridaStateLicense,
      agentCountyCityStateString
    );

    if (needsToSignConversionForm) {
      formsToSign.push({
        formType: 'Conversion',
        pdfUrl: this.controller.accountCenterController.getViewConversionPdfURL(),
        textPrompt: `Application for Simplified Conversion`,
        signFn: async () => {
          await this.controller.accountCenterController.getAndSignForm(primaryPolicyNumber, 'Conversion');
        },
      });
    }

    const SSN = await this.controller.getPiiValueByKey(policy.ownerPerson.SSN);
    const needsToSignPappForm: boolean = await this.controller.accountCenterController.customerNeedsToSignPappForm(
      primaryPolicyNumber,
      SSN
    );

    if (needsToSignPappForm) {
      formsToSign.push({
        formType: 'Papp',
        pdfUrl: this.controller.accountCenterController.getViewPappPdfURL(),
        textPrompt: `Pre-Authorized Payment Plan (PAPP)`,
        signFn: async () => {
          await this.controller.accountCenterController.getAndSignForm(primaryPolicyNumber, 'Papp');
        },
      });
    }

    const needsToSignConsentForm: boolean =
      await this.controller.accountCenterController.customerNeedsToSignConsentForm(primaryPolicyNumber);

    if (needsToSignConsentForm) {
      formsToSign.push({
        formType: 'Consent',
        pdfUrl: this.controller.accountCenterController.getViewConsentPdfURL(),
        textPrompt: `Electronic Signature and Delivery Consent`,
        signFn: async () => {
          await this.controller.accountCenterController.getAndSignForm(primaryPolicyNumber, 'Consent');
        },
      });
    }

    const illustrationRequest: IllustrationRequest =
      await this.controller.accountCenterController.createPolicyIllustrationRequest(
        this.controller.customer.application.primaryPolicy,
        CoverpathUtils.convertedFromSanderling(this.controller.customer.application.primaryPolicy)
      );

    const basicIllustrationPDF: Buffer = await this.basicIllustrationService.generateBasicIllustrationPDF(
      illustrationRequest,
      false
    );
    const needsToSignBasicIllustration: boolean =
      await this.controller.accountCenterController.customerNeedsToSignBasicIllustration(basicIllustrationPDF);

    if (needsToSignBasicIllustration) {
      formsToSign.push({
        formType: 'BasicIllustration',
        pdfUrl: this.controller.accountCenterController.getViewBasicIllustrationURL(),
        textPrompt: 'Basic Illustration',
        signFn: async () => {
          await this.controller.accountCenterController.getAndSignForm(primaryPolicyNumber, 'BasicIllustration');
        },
      });
    }
    return formsToSign;
  }

  async getAgentCountyCityStateString(zip: string) {
    const locationFinder = await this.controller.publicGetLocationByZip(zip);
    if (locationFinder && locationFinder.county) {
      return `${locationFinder.county}, ${locationFinder.city}, ${locationFinder.stateCode}`;
    } else {
      throw Error(`No FL county found for zip: ${zip}`);
    }
  }

  async signForm() {
    try {
      this.loadingScreen = true;
      this.esignDisabled = true;
      UILoggerSingleton.getInstance().info(
        {
          component: 'browser',
          module: 'ClientConversionEsignModalComponent',
          function: 'signForm',
          category: UICategory.request,
          data: {
            formType: this.formsToSign[this.docIndex].formType,
            policyNumber: this.primaryPolicyNumber,
            ...Utils.extractIds(this.accountCenterService.primaryPolicy, this.controller.customer),
          },
        },
        'esigning client conversion'
      );
      await this.formsToSign[this.docIndex].signFn();
      this.docIndex++;

      //show the next form if there is one, or accept the policy if thr last form was just signed
      if (this.docIndex < this.formsToSign.length) {
        await this.showPdf();
      } else {
        this.docIndex--;
        const primaryPolicy = this.accountCenterService.primaryPolicy as CoverpathBasePolicy;
        UILoggerSingleton.getInstance().info(
          {
            component: 'browser',
            module: 'ClientConversionEsignModalComponent',
            function: 'signForm',
            category: UICategory.request,
            data: {
              policyNumber: this.primaryPolicyNumber,
              ...Utils.extractIds(primaryPolicy, this.controller.customer),
            },
          },
          'Accepting offer'
        );

        this.accountCenterService.isLoadingSubject.next({
          isLoading: true,
          type: IsLoadingType.DURING,
          action: this.accountCenterService.latestActionString,
        });
        await this.accountCenterService.acceptFromService();

        UILoggerSingleton.getInstance().info({
          component: 'browser',
          module: 'ClientConversionEsignModalComponent',
          function: 'publicSave()',
          data: {
            policyNumber: this.primaryPolicyNumber,
            ...Utils.extractIds(primaryPolicy, this.controller.customer),
          },
          category: UICategory.request,
        });
        await this.controller.publicSave();
        await this.controller.accountCenterController.checkRequirement(
          this.accountCenterService.primaryPolicy,
          'ClientEsignCompleted',
          '',
          null
        );
        this.closeModal();
        const policy = this.accountCenterService.primaryPolicy;
        await this.accountCenterService.notifyAdvisorOnceClientSignsConversion(policy as CoverpathPolicy);
      }
    } catch (e) {
      const error = {
        isHumanRelated: false,
        data: {
          formType: this.formsToSign[this.docIndex].formType,
          policyNumber: this.primaryPolicyNumber,
          ...Utils.extractIds(this.accountCenterService.primaryPolicy, this.controller.customer),
        },
        name: e.name,
        message: e.message,
        stack: e.stack,
      };
      UILoggerSingleton.getInstance().error({
        module: 'ClientConversionEsignModalComponent',
        function: 'signForm',
        category: UICategory.request,
        error: error,
      });

      this.errorSigningPdf = true;
    } finally {
      this.loadingScreen = false;
    }
  }
}
